import * as THREE from 'three';

const FRAGMENT_LIFETIME = 2.6;
const FLOOR_Y = -1.1;

// Each shell panel is a solid wedge. The flying fragment uses the same
// vertices as the panel removed from the shell, so the break leaves a real gap.
export const createCoreFracture = (
  core,
  scene,
  material,
  wireMaterial,
  maxHp
) => {
  const surface = new THREE.IcosahedronGeometry(0.95, 1);
  const surfacePositions = surface.getAttribute('position');
  const shellPositions = [];
  const wirePositions = [];
  const fragments = [];
  const debris = new THREE.Group();
  scene.add(debris);

  for (let index = 0; index < surfacePositions.count; index += 3) {
    const vertices = [0, 1, 2].map((offset) =>
      new THREE.Vector3().fromBufferAttribute(surfacePositions, index + offset)
    );
    const center = vertices
      .reduce((sum, vertex) => sum.add(vertex), new THREE.Vector3())
      .divideScalar(3);
    const outer = vertices.map((vertex) => vertex.lerp(center, 0.018));
    const inner = outer.map((vertex) => vertex.clone().multiplyScalar(0.77));
    const positions = [];
    const edges = [];
    const triangle = (a, b, c) => {
      [a, b, c].forEach((vertex) => positions.push(...vertex.toArray()));
    };
    triangle(...outer);
    triangle(inner[2], inner[1], inner[0]);
    for (let side = 0; side < 3; side += 1) {
      const next = (side + 1) % 3;
      triangle(outer[side], inner[side], outer[next]);
      triangle(outer[next], inner[side], inner[next]);
      [outer[side], outer[next]].forEach((vertex) =>
        edges.push(...vertex.clone().multiplyScalar(1.006).toArray())
      );
    }

    const shellOffset = shellPositions.length;
    const wireOffset = wirePositions.length;
    shellPositions.push(...positions);
    wirePositions.push(...edges);
    const relativeToCenter = (values) =>
      values.map(
        (value, coordinate) => value - center.getComponent(coordinate % 3)
      );
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
      'position',
      new THREE.Float32BufferAttribute(relativeToCenter(positions), 3)
    );
    geometry.computeVertexNormals();
    const mesh = new THREE.Mesh(geometry, material.clone());
    mesh.castShadow = true;
    const edgeGeometry = new THREE.BufferGeometry();
    edgeGeometry.setAttribute(
      'position',
      new THREE.Float32BufferAttribute(relativeToCenter(edges), 3)
    );
    const wire = new THREE.LineSegments(edgeGeometry, wireMaterial.clone());
    const group = new THREE.Group();
    group.add(mesh, wire);
    group.visible = false;
    debris.add(group);
    fragments.push({
      group,
      mesh,
      wire,
      center,
      openCenter: center.clone(),
      openRotation: new THREE.Quaternion(),
      shellOffset,
      wireOffset,
      shellCount: positions.length,
      wireCount: edges.length,
      direction: center.clone().normalize(),
      velocity: new THREE.Vector3(),
      spin: new THREE.Vector3(
        1.4 + (index % 5),
        1.8 + (index % 7),
        1 + (index % 3)
      ),
      missing: false,
      age: 0,
      bounced: false,
    });
  }
  surface.dispose();

  const shellGeometry = new THREE.BufferGeometry();
  const shellAttribute = new THREE.Float32BufferAttribute(shellPositions, 3);
  shellAttribute.setUsage(THREE.DynamicDrawUsage);
  shellGeometry.setAttribute('position', shellAttribute);
  shellGeometry.computeVertexNormals();
  shellGeometry.computeBoundingSphere();
  const shell = new THREE.Mesh(shellGeometry, material);
  shell.castShadow = true;
  shell.receiveShadow = true;
  const wireGeometry = new THREE.BufferGeometry();
  const wireAttribute = new THREE.Float32BufferAttribute(wirePositions, 3);
  wireAttribute.setUsage(THREE.DynamicDrawUsage);
  wireGeometry.setAttribute('position', wireAttribute);
  wireGeometry.computeBoundingSphere();
  const wire = new THREE.LineSegments(wireGeometry, wireMaterial);
  core.add(shell, wire);

  let lastHp = maxHp;
  const localImpact = new THREE.Vector3();
  const worldRotation = new THREE.Quaternion();
  const worldScale = new THREE.Vector3();
  const outward = new THREE.Vector3();
  const vertex = new THREE.Vector3();
  const hinge = new THREE.Vector3(0, 1, 0);
  let openness = 0;

  const writePanel = (fragment) => {
    const {
      shellOffset,
      wireOffset,
      shellCount,
      wireCount,
      center,
      openCenter,
      openRotation,
    } = fragment;
    const write = (source, attribute, offset, count) => {
      for (
        let coordinate = offset;
        coordinate < offset + count;
        coordinate += 3
      ) {
        vertex
          .fromArray(source, coordinate)
          .sub(center)
          .applyQuaternion(openRotation)
          .add(openCenter);
        vertex.toArray(attribute.array, coordinate);
      }
    };
    write(shellPositions, shellAttribute, shellOffset, shellCount);
    write(wirePositions, wireAttribute, wireOffset, wireCount);
  };

  const setMissing = (fragment, missing) => {
    fragment.missing = missing;
    const { shellOffset, wireOffset, shellCount, wireCount } = fragment;
    if (missing) {
      shellAttribute.array.fill(0, shellOffset, shellOffset + shellCount);
      wireAttribute.array.fill(0, wireOffset, wireOffset + wireCount);
    } else {
      writePanel(fragment);
      fragment.group.visible = false;
    }
    shellAttribute.needsUpdate = true;
    wireAttribute.needsUpdate = true;
  };

  // Open the surviving armor as hinged petals. Damage still removes the same
  // panels, and fragments inherit this pose when they break away.
  const setOpenness = (value) => {
    const next = THREE.MathUtils.clamp(value, 0, 1.3);
    if (Math.abs(next - openness) < 0.0001) return;
    openness = next;
    fragments.forEach((fragment) => {
      const side = fragment.center.x >= 0 ? 1 : -1;
      fragment.openCenter
        .copy(fragment.center)
        .addScaledVector(fragment.direction, openness * 0.65);
      fragment.openCenter.x += side * openness * 0.5;
      fragment.openCenter.z -= openness * 1.1;
      fragment.openRotation.setFromAxisAngle(hinge, side * openness * 0.85);
      if (!fragment.missing) writePanel(fragment);
    });
    shellAttribute.needsUpdate = true;
    wireAttribute.needsUpdate = true;
    shellGeometry.computeVertexNormals();
    shellGeometry.computeBoundingSphere();
    wireGeometry.computeBoundingSphere();
  };

  const syncHealth = (hp) => {
    if (hp === lastHp) return;
    const missing = fragments.filter((fragment) => fragment.missing);
    const target = Math.ceil(
      (1 - THREE.MathUtils.clamp(hp / maxHp, 0, 1)) * fragments.length * 0.65
    );
    if (hp > lastHp) {
      missing.slice(target).forEach((fragment) => setMissing(fragment, false));
      shellGeometry.computeVertexNormals();
      shellGeometry.computeBoundingSphere();
      wireGeometry.computeBoundingSphere();
    } else if (missing.length < target) {
      // Scatter scars around the shell when loading an already damaged Core.
      const available = fragments.filter((fragment) => !fragment.missing);
      available.sort(
        (a, b) =>
          Math.sin(a.shellOffset * 12.9898) - Math.sin(b.shellOffset * 12.9898)
      );
      available
        .slice(0, target - missing.length)
        .forEach((fragment) => setMissing(fragment, true));
    }
    lastHp = hp;
  };

  const breakOff = (
    worldImpact,
    damage,
    reduceMotion = false,
    shatterAll = false
  ) => {
    if (damage <= 0) return 0;
    core.updateWorldMatrix(true, false);
    localImpact.copy(worldImpact);
    core.worldToLocal(localImpact).normalize();
    core.getWorldQuaternion(worldRotation);
    core.getWorldScale(worldScale);
    const count = THREE.MathUtils.clamp(
      Math.ceil((damage / maxHp) * fragments.length * 0.65),
      3,
      24
    );
    const available = fragments.filter((fragment) => !fragment.missing);
    available.sort(
      (a, b) => b.direction.dot(localImpact) - a.direction.dot(localImpact)
    );
    const selected = shatterAll ? available : available.slice(0, count);
    selected.forEach((fragment, index) => {
      setMissing(fragment, true);
      if (reduceMotion) return;
      const { group, mesh, wire } = fragment;
      group.position.copy(fragment.openCenter);
      core.localToWorld(group.position);
      group.quaternion.copy(worldRotation).multiply(fragment.openRotation);
      group.scale.copy(worldScale);
      group.visible = true;
      mesh.material.color.copy(material.color);
      mesh.material.emissive.copy(material.emissive);
      mesh.material.emissiveIntensity = 2.8;
      mesh.material.opacity = 1;
      wire.material.color.copy(wireMaterial.color);
      wire.material.opacity = 0.95;
      outward.copy(fragment.direction).applyQuaternion(worldRotation);
      fragment.velocity
        .copy(outward)
        .multiplyScalar(1.8 + Math.min(damage, 160) / 80 + (index % 4) * 0.24);
      fragment.velocity.y += 1.1 + (index % 3) * 0.2;
      fragment.age = 0;
      fragment.bounced = false;
    });
    return selected.length;
  };

  const update = (delta, reduceMotion = false) => {
    fragments.forEach((fragment) => {
      const { group, mesh, wire, velocity, spin } = fragment;
      if (!group.visible) return;
      fragment.age += delta;
      if (reduceMotion || fragment.age >= FRAGMENT_LIFETIME) {
        group.visible = false;
        return;
      }
      // Hold the first instant of the break so individual pieces can be read.
      const step = delta * (fragment.age < 0.2 ? 0.4 : 1);
      group.position.addScaledVector(velocity, step);
      velocity.y -= 2.8 * step;
      group.rotation.x += spin.x * step;
      group.rotation.y += spin.y * step;
      group.rotation.z += spin.z * step;
      if (group.position.y < FLOOR_Y) {
        group.position.y = FLOOR_Y;
        if (!fragment.bounced) {
          velocity.y = Math.abs(velocity.y) * 0.28;
          velocity.x *= 0.55;
          velocity.z *= 0.55;
          fragment.bounced = true;
        } else {
          velocity.set(0, 0, 0);
        }
      }
      const fade =
        1 - THREE.MathUtils.smoothstep(fragment.age, 1.4, FRAGMENT_LIFETIME);
      mesh.material.opacity = fade;
      wire.material.opacity = fade * 0.95;
    });
  };

  const shatterAll = (worldImpact, reduceMotion = false) =>
    breakOff(worldImpact, maxHp, reduceMotion, true);

  return {
    shell,
    wire,
    debris,
    syncHealth,
    setOpenness,
    breakOff,
    shatterAll,
    update,
  };
};
